import { useStore } from '@nanostores/react';
import { memo, useEffect, useRef } from 'react';
import { SearchResultItem } from './SearchResultItem';
import { commandStore } from '~/lib/stores/command';
import { type AnyCommand, type CommandContext } from '~/lib/stores/command/types';
import { userStore } from '~/lib/stores/user';

const SearchResultList = memo<{
  groups: { id: string; name: string; commands: { index: number; command: AnyCommand }[] }[];
  selectedIndex: number;
  commandContext: CommandContext;
}>(({ groups, selectedIndex, commandContext }) => {
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const autoScrollToSelectedItem = useStore(commandStore.autoScrollToSelectedItem);
  const loggedIn = useStore(userStore.loggedIn);

  useEffect(() => {
    if (selectedItemRef.current && autoScrollToSelectedItem) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
      });
    }
  }, [selectedIndex, autoScrollToSelectedItem]);

  return (
    <div
      className="p-2 grid gap-px content-start overflow-auto"
      role="listbox"
      aria-label="Available commands and prompts"
    >
      {groups.map((group) => (
        <div key={group.id} className="grid gap-px">
          <div className="text-xs font-medium text-bolt-elements-textSecondary pl-3 pt-2 pb-1.5">{group.name}</div>

          {group.commands.map(({ command, index }) => (
            <SearchResultItem
              key={`${command.id}-${group.id}`}
              command={command}
              index={index}
              showOptions={loggedIn}
              commandContext={commandContext}
              isSelected={index === selectedIndex}
              isFavorited={commandStore.isFavorited(command.id)}
              itemRef={index === selectedIndex ? selectedItemRef : undefined}
            />
          ))}
        </div>
      ))}
    </div>
  );
});

SearchResultList.displayName = 'SearchResultList';

export { SearchResultList };
